import C0250Validator from 'shared/ncts/p5/conditions/C0250'
import C0505Validator from 'shared/ncts/p5/conditions/C0505'
import NA60Validator from 'shared/ncts/EE/NA60'
import NA61Validator from 'shared/ncts/EE/NA61'
import NA62Validator from 'shared/ncts/EE/NA62'
import { hasText } from 'shared/lib/common-util'
import { FieldRequirementResolverContext } from '../types'
import { createFieldRequirementGetter, matchServerRequirement } from '../../../resolverFactory'
import {
  FieldRequirement,
  FieldRequirementEnvironment,
  FieldRequirementResolverMap,
  forbidden,
  optional,
  required,
  toFieldRequirement,
} from '../../../../../../models/fieldRules'
import C0001Validator from '../../../../../../../../shared/ncts/p5/conditions/C0001_server'

function ruleForConsigneeNameAndAddress(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  const consigneeRequirement = env.getFieldRequirement('consignee')
  if (consigneeRequirement === 'forbidden') return 'forbidden'

  if (ctx.country === 'RO') {
    return toFieldRequirement(C0250Validator.validateNameAndAddressRequirement(ctx.consignee?.identificationNumber))
  }

  return 'required'
}

function ruleForConsigneeAddressPostcode(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  const consigneeRequirement = env.getFieldRequirement('consignee')
  if (consigneeRequirement === 'forbidden') return 'forbidden'

  return toFieldRequirement(C0505Validator.validatePostcodeRequirement(ctx.consignee?.address?.country))
}

function ruleForConsignorNameAndAddress(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  const consignorRequirement = env.getFieldRequirement('consignor')
  if (consignorRequirement === 'forbidden') return 'forbidden'

  if (ctx.country === 'RO') {
    return toFieldRequirement(C0250Validator.validateNameAndAddressRequirement(ctx.consignor?.identificationNumber))
  }

  return 'required'
}

function ruleForConsignorAddressPostcode(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  return toFieldRequirement(C0505Validator.validatePostcodeRequirement(ctx.consignor?.address?.country))
}

function ruleForConsignorContactPerson(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  if (ctx.country === 'LT' && hasText(ctx.consignor?.identificationNumber)) {
    return 'required'
  }

  return 'forbidden'
}

function ruleForPreviousDocumentDocumentType(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  if (ctx.country !== 'EE') return 'required'

  return toFieldRequirement(NA60Validator.validatePreviousDocumentDocumentTypeRequirement(ctx.previousDocument))
}

function ruleForPreviousDocumentProcedureType(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  if (ctx.country !== 'EE') return 'forbidden'

  const documentTypeRequirement = ruleForPreviousDocumentDocumentType(env)
  if (documentTypeRequirement === 'forbidden') return 'optional'

  return toFieldRequirement(NA60Validator.validatePreviousDocumentDocumentTypeRequirement(ctx.previousDocument))
}

function ruleForPreviousDocumentReferenceNumber(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  const { ctx } = env

  if (ctx.country !== 'EE') return 'required'

  let requirement = toFieldRequirement(NA61Validator.validatePreviousDocumentRefNumberRequirement(ctx.previousDocument))
  if (requirement === 'optional') {
    requirement = toFieldRequirement(NA62Validator.validatePreviousDocumentReferenceNumberRequirement(ctx.previousDocument))

    if (requirement === 'forbidden' && hasText(ctx.previousDocument?.referenceNumber)) {
      requirement = 'optional'
    }
  }

  return requirement
}

function ruleForConsignee(env: FieldRequirementEnvironment<FieldRequirementResolverContext>): FieldRequirement {
  env.ignoreServerRequirement()

  const { ctx } = env

  const ruleConditions = ctx.serverValidationResult?.ruleConditions

  return toFieldRequirement(C0001Validator.validateConsigneeRequirementForHouseConsignment({
    security: ctx.security,
    countryOfDestination: ruleConditions?.consignmentCountryOfDestination ?? '',
    houseCountryOfDestination: ctx.houseCountryOfDestination,
    ...ruleConditions,
  }))
}

const fieldRequirementResolvers: FieldRequirementResolverMap<FieldRequirementResolverContext> = {
  additionalInformation: forbidden,
  additionalReference: forbidden,
  additionalSupplyChainActor: forbidden,
  buyer: forbidden,
  consignee: ruleForConsignee,
  'consignee.address': ruleForConsigneeNameAndAddress,
  'consignee.address.postcode': ruleForConsigneeAddressPostcode,
  'consignee.address.street': forbidden,
  'consignee.address.streetNumber': forbidden,
  'consignee.contactPerson': forbidden,
  'consignee.typeOfPerson': forbidden,
  consignor: optional, // server-side
  'consignor.address': ruleForConsignorNameAndAddress,
  'consignor.address.postcode': ruleForConsignorAddressPostcode,
  'consignor.address.street': forbidden,
  'consignor.address.streetNumber': forbidden,
  'consignor.contactPerson': ruleForConsignorContactPerson,
  'consignor.typeOfPerson': forbidden,
  containerIndicator: forbidden,
  countriesOfRouting: forbidden,
  countryOfDestination: optional, // server-side
  countryOfDispatch: optional, // server-side
  departureTransportMeans: forbidden, // server-side
  differentCountriesOfRouting: forbidden,
  differentBuyer: forbidden,
  differentPlaceOfAcceptance: forbidden,
  differentPlaceOfDelivery: forbidden,
  differentSeller: forbidden,
  grossMass: required,
  passiveBorderTransportMeans: forbidden,
  placeOfAcceptance: forbidden,
  placeOfDelivery: forbidden,
  'previousDocument.*.documentType': ruleForPreviousDocumentDocumentType,
  'previousDocument.*.procedureType': ruleForPreviousDocumentProcedureType,
  'previousDocument.*.referenceNumber': ruleForPreviousDocumentReferenceNumber,
  referenceNumberUCR: optional, // TODO: C0502 server-side T1-3141
  seller: forbidden,
  'supportingDocument.*.documentType': required,
  'supportingDocument.*.referenceNumber': required,
  transportChargesMethodOfPayment: forbidden,
  'transportDocument.*.documentType': required,
  'transportDocument.*.referenceNumber': required,
  transportEquipment: forbidden,
  transportMeans: optional, // TODO: C0826 server-side T1-3141
}

function getServerRequirementForPath(
  path: string,
  ctx: FieldRequirementResolverContext,
) {
  return matchServerRequirement(ctx.serverValidationResult?.fieldRequirements, path)
}

const getFieldRequirementP5 = createFieldRequirementGetter<FieldRequirementResolverContext>(
  fieldRequirementResolvers,
  getServerRequirementForPath,
)

export default getFieldRequirementP5
